import axios from 'axios';
import { AuthResponse, ApiResponse, GitLog, DashboardStats, ProjectStats, Project, ProjectDetails, Robot, Compute } from '../types';
import { OptimizedApiService } from './optimizedApi';

export const API_URL = '/api';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '10000');

type AuthCallbackVerificationData = {
  valid: boolean;
  userId: string;
  email?: string;
  frontendUrl?: string;
  expiresAt?: number;
};

const api = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event('auth:session-expired'));
    }
    return Promise.reject(error);
  }
);

// Initialize optimized API service
export const optimizedApi = OptimizedApiService.getInstance(api);

export const authAPI = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  register: async (name: string, email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', { name, email, password });
    return response.data;
  },
  
  getMe: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  verifyCallbackProof: async (loginProof: string): Promise<ApiResponse<AuthCallbackVerificationData>> => {
    const response = await api.get('/auth/callback/verify', {
      params: {
        login_proof: loginProof,
      },
    });
    return response.data;
  },

  logout: async (): Promise<ApiResponse<any>> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
};

export const gitLogAPI = {
  getGitLogs: async (params?: {
    page?: number;
    limit?: number;
    branch?: string;
    startDate?: string;
    endDate?: string;
    robotId?: string;
    computeId?: string;
    projectName?: string;
    isRobot?: boolean;
    branchName?: string;
    authorName?: string;
  }): Promise<ApiResponse<GitLog[]>> => {
    const response = await api.get('/gitlogs', { params });
    return response.data;
  },
  
  getGitLogsByRobot: async (robotId: string, params?: {
    page?: number;
    limit?: number;
    computeId?: string;
    projectName?: string;
    branchName?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{ robotId: string; commits: GitLog[]; pagination: any }>> => {
    const response = await api.get(`/gitlogs/robot/${robotId}`, { params });
    return response.data;
  },
  
  getGitLogsByCompute: async (computeId: string, params?: {
    page?: number;
    limit?: number;
    projectName?: string;
    branchName?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{ computeId: string; commits: GitLog[]; pagination: any }>> => {
    const response = await api.get(`/gitlogs/compute/${computeId}`, { params });
    return response.data;
  },
  
  getGitLog: async (id: string): Promise<ApiResponse<GitLog>> => {
    const response = await api.get(`/gitlogs/${id}`);
    return response.data;
  },
  
  createGitLog: async (gitLog: Partial<GitLog>): Promise<ApiResponse<GitLog>> => {
    const response = await api.post('/gitlogs', gitLog);
    return response.data;
  },
  
  getDashboardStats: async (): Promise<ApiResponse<DashboardStats>> => {
    return optimizedApi.cachedGet('/gitlogs/stats', undefined, 60000); // 1 minute cache
  },
  
  getProjectStats: async (): Promise<ApiResponse<ProjectStats>> => {
    const response = await api.get('/gitlogs/projects');
    return response.data;
  },

  updateFileVerification: async (commitId: string, fileId: string, verifiedCode: boolean, verifiedBy?: string): Promise<ApiResponse<any>> => {
    const response = await api.put(`/gitlogs/${commitId}/files/${fileId}/verify`, { 
      verifiedCode, 
      verifiedBy: verifiedBy || 'Current User' 
    });
    return response.data;
  },

  getVerifiedFiles: async (commitId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/gitlogs/${commitId}/verified-files`);
    return response.data;
  },

  getApplicationFolders: async (projectName: string): Promise<ApiResponse<any>> => {
    return optimizedApi.cachedGet(`/gitlogs/${projectName}/application-folders`, undefined, 300000); // 5 minutes cache
  },

  getFolderGitHistory: async (projectName: string, folderName: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> => {
    const response = await api.get(`/gitlogs/${projectName}/folder/${folderName}/history`, { params });
    return response.data;
  },

  getSystemParametersHistory: async (projectName: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> => {
    return optimizedApi.cachedGet(`/gitlogs/${projectName}/system-parameters`, params, 180000); // 3 minutes cache
  },

  getSystemParametersByIpAddress: async (ipAddress: string, isRobot?: boolean): Promise<ApiResponse<any>> => {
    const response = await api.post('/system-parameters/by-ip', { ipAddress, isRobot });
    return response.data;
  },
};

export const projectsAPI = {
  getProjects: async (isRobot?: boolean): Promise<ApiResponse<Project[]>> => {
    const params = isRobot !== undefined ? { isRobot: isRobot.toString() } : {};
    return optimizedApi.cachedGet('/projects', params, 300000); // 5 minutes cache
  },

  getProjectById: async (id: string): Promise<ApiResponse<ProjectDetails>> => {
    return optimizedApi.cachedGet(`/projects/${id}`, undefined, 300000); // 5 minutes cache
  },

  getServerIpAddresses: async (): Promise<ApiResponse<any[]>> => {
    return optimizedApi.cachedGet('/projects/servers/ip-addresses', undefined, 300000); // 5 minutes cache
  },

  getProjectsByIpAddress: async (ipAddress: string, isRobot?: boolean): Promise<ApiResponse<Project[]>> => {
    // Encode IP address to handle IPv6 addresses with colons (e.g., ::1)
    const encodedIpAddress = encodeURIComponent(ipAddress);
    const response = await api.post(`/projects/by-ip/${encodedIpAddress}`, { isRobot });
    return response.data;
  },

  saveCustomName: async (ipAddress: string, customName: string, type: 'robot' | 'server'): Promise<ApiResponse<any>> => {
    const encodedIpAddress = encodeURIComponent(ipAddress);
    const response = await api.post(`/projects/custom-name/${encodedIpAddress}`, { 
      customName, 
      type 
    });
    return response.data;
  },
};

export const currentChangesAPI = {
  saveCurrentChanges: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.post('/current-changes', data);
    return response.data;
  },

  getCurrentChanges: async (projectName: string, params?: {
    branchName?: string;
    workingDirectory?: string;
  }): Promise<ApiResponse<any>> => {
    const response = await api.get(`/current-changes/${projectName}`, { params });
    return response.data;
  },

  getCurrentChangesByIpAddress: async (ipAddress: string, isRobot?: boolean): Promise<ApiResponse<any>> => {
    // Encode IP address to handle IPv6 addresses with colons (e.g., ::1)
    const encodedIpAddress = encodeURIComponent(ipAddress);
    const params = isRobot !== undefined ? { isRobot: isRobot.toString() } : {};
    const response = await api.get(`/current-changes/by-ip/${encodedIpAddress}`, { params });
    return response.data;
  },

  getTrackedFilesByIpAddress: async (ipAddress: string, isRobot?: boolean): Promise<ApiResponse<any>> => {
    // Encode IP address to handle IPv6 addresses with colons (e.g., ::1)
    const encodedIpAddress = encodeURIComponent(ipAddress);
    const params = isRobot !== undefined ? { isRobot: isRobot.toString() } : {};
    const response = await api.get(`/tracked-files/by-ip/${encodedIpAddress}`, { params });
    return response.data;
  },


  getFileContent: async (projectName: string, fileName: string, params?: {
    branchName?: string;
    workingDirectory?: string;
  }): Promise<ApiResponse<any>> => {
    // Encode fileName to handle special characters and extensions
    const encodedFileName = encodeURIComponent(fileName);
    const response = await api.get(`/current-changes/${projectName}/file/${encodedFileName}`, { params });
    return response.data;
  },

  getAllCurrentChanges: async (params?: {
    page?: number;
    limit?: number;
    projectName?: string;
    branchName?: string;
    gitStatus?: string;
  }): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/current-changes', { params });
    return response.data;
  },

  deleteCurrentChanges: async (projectName: string, params?: {
    branchName?: string;
    workingDirectory?: string;
  }): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/current-changes/${projectName}`, { params });
    return response.data;
  },

  getSystemParametersFromCurrentChanges: async (projectName: string, params?: {
    branchName?: string;
    workingDirectory?: string;
  }): Promise<ApiResponse<any>> => {
    const response = await api.get(`/current-changes/${projectName}/system-parameters`, { params });
    return response.data;
  },

  updateFileVerification: async (projectName: string, fileId: string, verifiedCode: boolean, verifiedBy?: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(`/current-changes/${projectName}/files/${fileId}/verify`, { 
        verifiedCode, 
        verifiedBy: verifiedBy || 'Current User' 
      });
      
      return response.data;
    } catch (error: any) {
      console.error('API Error in updateFileVerification:', error);
      throw error;
    }
  },
};

export const verifiedCodeAPI = {
  saveVerifiedCode: async (data: {
    projectName: string;
    fileName: string;
    filePath?: string;
    branchName?: string;
    commitHash?: string;
    verifiedContent: string;
    verifiedDiff?: string;
    fileSize?: number;
    linesOfCode?: number;
    verifiedBy: string;
    verificationNotes?: string;
    originalFileInfo?: any;
  }): Promise<ApiResponse<any>> => {
    const response = await api.post('/verified-code', data);
    return response.data;
  },

  getVerifiedCode: async (projectName: string, fileName: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/verified-code/${projectName}/${fileName}`);
    return response.data;
  },

  getProjectVerifiedCodes: async (projectName: string, params?: {
    branchName?: string;
  }): Promise<ApiResponse<any>> => {
    const response = await api.get(`/verified-code/${projectName}`, { params });
    return response.data;
  },

  checkVerifiedCode: async (projectName: string, fileName: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/verified-code/${projectName}/${fileName}/check`);
    return response.data;
  },

  getVerificationStats: async (projectName: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/verified-code/${projectName}/stats`);
    return response.data;
  },

  deleteVerifiedCode: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/verified-code/${id}`);
    return response.data;
  },
};

export const robotsAPI = {
  getRobots: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<Robot[]>> => {
    const response = await api.get('/robots', { params });
    return response.data;
  },
  
  getRobotById: async (id: string): Promise<ApiResponse<Robot>> => {
    const response = await api.get(`/robots/${id}`);
    return response.data;
  },
  
  createRobot: async (robot: Partial<Robot>): Promise<ApiResponse<Robot>> => {
    const response = await api.post('/robots', robot);
    return response.data;
  },
  
  updateRobot: async (id: string, robot: Partial<Robot>): Promise<ApiResponse<Robot>> => {
    const response = await api.put(`/robots/${id}`, robot);
    return response.data;
  },
  
  deleteRobot: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/robots/${id}`);
    return response.data;
  },
};

export const computesAPI = {
  getComputes: async (params?: {
    page?: number;
    limit?: number;
    robotId?: string;
    computeType?: string;
    status?: string;
    projectName?: string;
  }): Promise<ApiResponse<Compute[]>> => {
    const response = await api.get('/computes', { params });
    return response.data;
  },
  
  getComputeById: async (id: string): Promise<ApiResponse<Compute>> => {
    const response = await api.get(`/computes/${id}`);
    return response.data;
  },
  
  createCompute: async (compute: Partial<Compute>): Promise<ApiResponse<Compute>> => {
    const response = await api.post('/computes', compute);
    return response.data;
  },
  
  updateCompute: async (id: string, compute: Partial<Compute>): Promise<ApiResponse<Compute>> => {
    const response = await api.put(`/computes/${id}`, compute);
    return response.data;
  },
  
  deleteCompute: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/computes/${id}`);
    return response.data;
  },
  
  getComputesByRobot: async (robotId: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Compute[]>> => {
    const response = await api.get('/computes', { params: { ...params, robotId } });
    return response.data;
  },
};

export default api;
